import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, DatePicker, Select, message, Spin, Row, Col, Statistic } from 'antd';
import { ReloadOutlined, CalculatorOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getApiEndpoint } from '../../../config/apiBase';
import { formatIndianNumber } from '../../../utils/numberFormat';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { MonthPicker } = DatePicker;

const UserMonthlySMSReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counting, setCounting] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const [summaryStats, setSummaryStats] = useState({});
  const [users, setUsers] = useState([]);

  // Fetch users for dropdown
  useEffect(() => {
    fetchUsers();
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (users.length > 0) {
      fetchMonthlyData();
    }
  }, [selectedUser, selectedMonth, currentPage, pageSize]);

  const fetchUsers = async () => {
    try {
      // Mock users - replace with actual API call
      const mockUsers = [
        { id: 9, username: 'nimich' },
        { id: 10, username: 'user2' },
        { id: 11, username: 'user3' },
      ];
      setUsers(mockUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchMonthlyData = async () => {
    setLoading(true);
    try {
      let url = getApiEndpoint('/reports/monthly-sms');
      
      if (selectedMonth && !selectedUser) {
        // Get all users for specific month
        url += `/${selectedMonth}`;
      } else if (selectedUser && !selectedMonth) {
        // Get specific user all months
        url += `/user/${selectedUser}/monthly-sms`;
      } else if (selectedUser && selectedMonth) {
        // Get specific user for specific month
        url += `/user/${selectedUser}/monthly-sms`;
      }
      
      url += `?page=${currentPage}&limit=${pageSize}`;
      
      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
        setTotalRecords(result.total || 0);
        
        // Calculate summary statistics
        calculateSummaryStats(result.data || []);
      } else {
        message.error('Failed to fetch monthly data');
      }
    } catch (error) {
      console.error('Error fetching monthly data:', error);
      message.error('Error fetching monthly data');
    } finally {
      setLoading(false);
    }
  };

  const calculateSummaryStats = (data) => {
    if (!data || data.length === 0) {
      setSummaryStats({});
      return;
    }

    const stats = data.reduce((acc, item) => {
      acc.totalSMS += item.sms_count || 0;
      acc.totalSent += item.sent || 0;
      acc.totalDelivered += item.delivered || 0;
      acc.totalFailed += item.failed || 0;
      acc.totalUsers += 1;
      return acc;
    }, {
      totalSMS: 0,
      totalSent: 0,
      totalDelivered: 0,
      totalFailed: 0,
      totalUsers: 0,
    });

    setSummaryStats(stats);
  };

  const handleRuntimeCount = async () => {
    setCounting(true);
    try {
      // Call the aggregation API or trigger the background job
      const response = await fetch(getApiEndpoint('/reports/aggregate-monthly'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          month: selectedMonth,
          user_id: selectedUser,
        }),
      });

      if (response.ok) {
        message.success('Runtime aggregation completed successfully!');
        // Refresh data after aggregation
        fetchMonthlyData();
      } else {
        message.error('Failed to run runtime aggregation');
      }
    } catch (error) {
      console.error('Error during runtime count:', error);
      message.error('Error during runtime count');
    } finally {
      setCounting(false);
    }
  };

  const exportToCSV = () => {
    // Create CSV content
    const headers = ['User ID', 'User Name', 'Month', 'SMS Count', 'Sent', 'Delivered', 'Failed', 'Pending'];
    const csvContent = [
      headers.join(','),
      ...data.map(item => [
        item.user_id,
        item.user_name,
        item.report_month,
        item.sms_count,
        item.sent,
        item.delivered,
        item.failed,
        item.pending
      ].join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `monthly_sms_summary_${selectedMonth || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 100,
    },
    {
      title: 'User Name',
      dataIndex: 'user_name',
      key: 'user_name',
    },
    {
      title: 'Month',
      dataIndex: 'report_month',
      key: 'report_month',
      render: (month) => {
        const date = dayjs(month, 'YYYY-MM');
        return date.format('MMMM YYYY');
      },
    },
    {
      title: 'SMS Count',
      dataIndex: 'sms_count',
      key: 'sms_count',
      render: (value) => formatIndianNumber(value),
      align: 'right',
      sorter: (a, b) => a.sms_count - b.sms_count,
    },
    {
      title: 'Sent',
      dataIndex: 'sent',
      key: 'sent',
      render: (value) => formatIndianNumber(value),
      align: 'right',
      sorter: (a, b) => a.sent - b.sent,
    },
    {
      title: 'Delivered',
      dataIndex: 'delivered',
      key: 'delivered',
      render: (value) => formatIndianNumber(value),
      align: 'right',
      sorter: (a, b) => a.delivered - b.delivered,
    },
    {
      title: 'Failed',
      dataIndex: 'failed',
      key: 'failed',
      render: (value) => (
        <span style={{ color: value > 0 ? '#ff4d4f' : 'inherit' }}>
          {formatIndianNumber(value)}
        </span>
      ),
      align: 'right',
      sorter: (a, b) => a.failed - b.failed,
    },
    {
      title: 'Pending',
      dataIndex: 'pending',
      key: 'pending',
      render: (value) => formatIndianNumber(value),
      align: 'right',
      sorter: (a, b) => a.pending - b.pending,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card title="Monthly SMS Summary Report" style={{ marginBottom: '16px' }}>
        {/* Filters Section */}
        <Space style={{ marginBottom: '16px' }}>
          <Select
            style={{ width: 200 }}
            placeholder="Select User"
            allowClear
            value={selectedUser}
            onChange={setSelectedUser}
          >
            {users.map(user => (
              <Option key={user.id} value={user.id}>
                {user.username}
              </Option>
            ))}
          </Select>
          
          <MonthPicker
            placeholder="Select Month"
            value={selectedMonth ? dayjs(selectedMonth, 'YYYY-MM') : null}
            onChange={(date) => {
              setSelectedMonth(date ? date.format('YYYY-MM') : null);
              setCurrentPage(1);
            }}
            format="MMMM YYYY"
          />
          
          <Button 
            type="primary" 
            icon={<CalculatorOutlined />}
            loading={counting}
            onClick={handleRuntimeCount}
          >
            Runtime Count
          </Button>
          
          <Button 
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={fetchMonthlyData}
          >
            Refresh
          </Button>
          
          <Button 
            icon={<DownloadOutlined />}
            onClick={exportToCSV}
            disabled={data.length === 0}
          >
            Export CSV
          </Button>
        </Space>

        {/* Summary Statistics */}
        {Object.keys(summaryStats).length > 0 && (
          <Row gutter={16} style={{ marginBottom: '16px' }}>
            <Col span={4}>
              <Statistic
                title="Total Users"
                value={summaryStats.totalUsers}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={5}>
              <Statistic
                title="Total SMS"
                value={summaryStats.totalSMS}
                formatter={(value) => formatIndianNumber(value)}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={5}>
              <Statistic
                title="Total Sent"
                value={summaryStats.totalSent}
                formatter={(value) => formatIndianNumber(value)}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={5}>
              <Statistic
                title="Total Delivered"
                value={summaryStats.totalDelivered}
                formatter={(value) => formatIndianNumber(value)}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={5}>
              <Statistic
                title="Total Failed"
                value={summaryStats.totalFailed}
                formatter={(value) => formatIndianNumber(value)}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Col>
          </Row>
        )}

        {/* Data Table */}
        <Table
          columns={columns}
          dataSource={Array.isArray(data) ? data : []}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRecords,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
            onChange: (page, newPageSize) => {
              setCurrentPage(page);
              setPageSize(newPageSize);
            },
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
};

export default UserMonthlySMSReport;
